import {
  Community,
  CommunityFeed,
  CommunityFeedType,
  FeedItem,
} from "@dreambigger/shared/src/types";
import React, { useCallback, useEffect, useMemo } from "react";
import { BoardProps } from "./board-wrapper";
import useSWRInfinite from "swr/infinite";
import { Spin } from "@dreambigger/design-system";
import {
  CollectionCard,
  CommunityGoal,
  CtaCard,
  EventCard,
  FundraiserCard,
  Hero,
  StoryCtaCard,
} from "..";
import StoryCard from "@dreambigger/shared/src/components/board/feed-cards/story-card";
import Masonry from "react-masonry-css";
import styles from "@dreambigger/shared/src/components/board/styles/react-masonry.module.scss";
import InfiniteScroll from "react-infinite-scroll-component";
import { communityPaths } from "@dreambigger/shared/src/api/communities";
import useUser from "@dreambigger/shared/src/hooks/use-user";
import {
  api,
  tokenHelper,
} from "@dreambigger/shared/src/api/weserve/api-helper";
import {
  useMixpanel,
  useGA,
  useAppcues,
  useSegment,
} from "@dreambigger/shared/src/hooks";
import { useRouter } from "next/router";
import InspireAnalytics, {
  InspireAnalyticsHelper,
  InspireAnalyticEvents,
} from "@dreambigger/shared/src/utils/inspire-analytics-helper";
import {
  boardFetcherFn,
  MASONRY_DEFAULTS_COLS,
} from "@dreambigger/shared/src/types/board";

interface FeedBoardProps extends BoardProps {
  initialData?: CommunityFeed[];
}

const FeedBoard: React.FC<FeedBoardProps> = ({
  community,
  initialData: initialFeed,
}) => {
  const [userLoading, { data: me }] = useUser(api, tokenHelper);
  const router = useRouter();

  // Load analytics hooks
  const googleAnalytics = useGA();
  const mixpanel = useMixpanel();
  const segment = useSegment();

  // Load appcues hook
  const appcues = useAppcues();

  // Check if community allows comments on stories.
  const showStoryComments =
    community.organization?.meta?.showInspireComments || false;

  /**
   *
   * Get the values to display in CommunityGoal if externalMatch is present for the community
   *  1. A Subcommunity will use the value from its own meta.
   *  2. A Regular Community will use the value from its organization's meta.
   */
  const getCommunityGoalValues = (community: Community) => {
    // Subcommunity
    if (community.subjectType === "Group") {
      return community.meta?.externalMatch;
    }

    // Regular Community
    if (community.subjectType === "Organization") {
      return community.organization?.meta?.externalMatch;
    }

    return;
  };

  const communityGoalValues = getCommunityGoalValues(community);

  const { data, setSize: setPageIndex } = useSWRInfinite<CommunityFeed>(
    (dataIndex, previousPageData) => {
      if (dataIndex === 0) return communityPaths.getFeed(community.slug);
      const nextParams = previousPageData?.next;
      return communityPaths.getFeed(community.slug, nextParams);
    },
    boardFetcherFn,
    {
      revalidateFirstPage: false,
      fallbackData: initialFeed,
    }
  );

  const feedData = data || initialFeed;

  const feed = useMemo(() => {
    if (!feedData) {
      return {
        records: [],
        totalRecords: 0,
      };
    }

    // useSWRInfinite returns data in an array of record arrays, i.e. =>
    // [{records: [{id: 1, ...}, {id: 2, ...}], totalRecords: 18}, {records: [{id: 2, ...}, {id: 3, ...}], totalRecords: 18}]
    // We need to flatten these records to create 1 array of records.
    const totalRecords: number = data?.[0]?.totalRecords || 0;

    const records = feedData.reduce(
      (previousValue, currentData) => [
        ...previousValue,
        ...(currentData?.records || []),
      ],
      [] as FeedItem[]
    );

    return {
      records,
      next: feedData.length > 0 ? feedData[feedData.length - 1].next || "" : "",
      totalRecords,
    };
  }, [feedData]);

  const cards = useMemo(
    () =>
      feed.records.map((feedItem, index) => {
        switch (feedItem.type) {
          case CommunityFeedType.Event:
            return feedItem.isFundraiser ? (
              <FundraiserCard
                key={feedItem.id}
                feedItem={feedItem}
                community={community}
                currentUser={me}
              />
            ) : (
              <EventCard
                key={feedItem.id}
                feedItem={feedItem}
                currentUser={me}
              />
            );
          case CommunityFeedType.FeedCTA:
            return (
              <CtaCard
                key={index}
                feedItem={feedItem}
                firstInFeed={index === 0}
              />
            );
          case CommunityFeedType.Story:
            return (
              <StoryCard
                key={feedItem.id}
                feedItem={feedItem}
                currentUser={me}
                userLoading={userLoading}
                showComments={showStoryComments}
              />
            );
          case CommunityFeedType.Collection:
            return (
              <CollectionCard
                key={feedItem.id}
                feedItem={feedItem}
                currentUser={me}
              />
            );
          default:
            console.warn(
              feedItem.type +
                " does not have a component built out for it and is not being rendered"
            );
            return null;
        }
      }),
    [feed, userLoading, showStoryComments, me]
  );

  const loadNextPage = useCallback(() => {
    // Load additional content and record a pagination change.
    setPageIndex((val) => {
      googleAnalytics.event({
        category: `Feed Pagination - community.detail`,
        action: `Scrolled`,
        label: "Paginate: community:loading",
        value: 1,
      });
      return val + 1;
    });
  }, [feed]);

  // Track the page view for analytics and appcues.
  useEffect(() => {
    // Give GoogleAnalytics time to initialize
    setTimeout(() => {
      // Wait for a response on whether a user is logged in.
      if (userLoading) {
        return;
      }
      googleAnalytics.setDefaultDimensions(community.slug, me);
      googleAnalytics.pageview(window.location.pathname);
      InspireAnalytics.track({
        eventName: InspireAnalyticEvents.PAGE_VIEW,
        properties: {
          Page: window.location.pathname,
          "Page Type": InspireAnalyticsHelper.getPageType(router.route),
          "Host Name": window.location.host,
        },
        mixpanel,
        segment,
      });
      // If there is a user, track user page visit on appcues
      if (me?.id) {
        appcues.identify(me.id.toString());
      }
    }, 500);
  }, [userLoading]);

  return (
    <>
      {/* HERO - Community header */}
      {community.meta?.stats && (
        <Hero
          communityStats={community.meta.stats}
          goalPresent={communityGoalValues && true}
        />
      )}
      {/* COMMUNITY GOAL - Next target goal and progress bar */}
      {communityGoalValues && (
        <div>
          <CommunityGoal
            title={communityGoalValues.matchHeadline || ""}
            subtitle={communityGoalValues.matchSubheadline || ""}
            ctaText={communityGoalValues.matchButtonText || ""}
            ctaLink={communityGoalValues.matchButtonURl || ""}
            secondaryCtaLink={communityGoalValues.matchSecondaryCtaUrl || ""}
            secondaryCtaText={communityGoalValues.matchSecondaryCtaText || ""}
            totalDonated={parseInt(community.totalDonated) || 0}
            nextGoal={communityGoalValues.matchNextGoal || ""}
            nextGoalText={communityGoalValues.matchNextGoalSubheadline || ""}
            sponsorText={communityGoalValues.matchSponsorText || ""}
            sponsorLogo={communityGoalValues.matchSponsorIcon || ""}
          />
        </div>
      )}
      <div className="pt-5">
        <InfiniteScroll
          dataLength={cards.length}
          next={loadNextPage}
          hasMore={!!feed.next}
          loader={
            <Spin
              size="large"
              className="w-100 mv-9 primary"
              tip="Loading More..."
            />
          }
          className="overflow-visible"
        >
          {/* COMMUNITY BOARD */}
          {/* Hide story cta card if configured to "hideStoryShareCta" or if board is a subcommunity */}
          {/* NOTE: Had to write the logic out like this with two Masonry component calls due to JSX fragment ruining masonry styling if trying to fit under one */}
          {!community?.meta?.options?.hideStoryShareCTA &&
          community.subjectType === "Organization" ? (
            // Display card if the community has not been configured to hide this card.
            <Masonry
              breakpointCols={MASONRY_DEFAULTS_COLS}
              className={styles.grid}
              columnClassName={styles.column}
            >
              {/* Story CTA Card - Uses z positioning placeholder divs to simulate one column spreading across two columns */}
              {/* over Masonry, which only supports single column natively. See included Masonary css file for media queries. */}
              <div
                className="z-0 relative"
                style={{ height: "210px", width: "100%", minWidth: "330px" }}
              >
                <div
                  className={`${styles.shrinkOnMobile} ${styles.twoCardWidth} z-1 absolute`}
                  style={{ height: "210px" }}
                >
                  <StoryCtaCard
                    cardTitle={
                      community?.organization?.meta?.storyCtaCard?.title ||
                      "Have you been a part of impacting your community?"
                    }
                    cardText={
                      community?.organization?.meta?.storyCtaCard?.subtitle ||
                      "Join us in sharing your story and inspiring others!"
                    }
                    buttonText={
                      community?.organization?.meta?.storyCtaCard?.buttonText ||
                      "Share Your Story"
                    }
                    buttonUrl={`/share/community/${community.slug}`}
                  />
                </div>
              </div>
              <div
                className={`${styles.hideOnMobile} z-0`}
                style={{ height: "210px", width: "100%", minWidth: "330px" }}
              />
              {cards}
            </Masonry>
          ) : (
            <Masonry
              breakpointCols={MASONRY_DEFAULTS_COLS}
              className={styles.grid}
              columnClassName={styles.column}
            >
              {cards}
            </Masonry>
          )}
        </InfiniteScroll>
      </div>
    </>
  );
};

export default FeedBoard;
